import type { Au08RuleSet } from "./types.js";

// AU-08 ruleset for FY2025-26 (1 July 2025 – 30 June 2026).
//
// Figures transcribed from the ATO MLS income thresholds + rates table and
// the privatehealth.gov.au Lifetime Health Cover rules. Verify against the
// sources below before each FY rollover; never edit a shipped ruleset to
// "fix" a later year — author a new fy20XX-XX.ts and register it.
export const fy2025_26: Au08RuleSet = {
  product: "AU08",
  fy: "2025-26",
  effectiveFrom: "2025-07-01",
  effectiveTo: "2026-06-30",
  assessmentYear: 2025,
  sources: {
    mls: "https://www.ato.gov.au/individuals-and-families/medicare-and-private-health-insurance/medicare-levy-surcharge/medicare-levy-surcharge-income-thresholds-and-rates",
    // ATO M2 (Individual tax return 2026 instructions) — the "Alice and Adam"
    // family worked example: combined MLS income $239,000 is above the family
    // Tier 2 threshold ($236,000), so the surcharge is levied at 1.25% on each
    // uncovered spouse's own income. Confirms (a) tier set by COMBINED income,
    // (b) levy on OWN income, (c) $239k sits in Tier 2 → the Tier 2/3 family
    // boundary is above $239k (it is $316,001, see mlsThresholds).
    mlsFamilyExample:
      "https://www.ato.gov.au/forms-and-instructions/tax-return-for-individuals-2026/medicare-levy-questions-m1-m2-individual-tax-return-2026/m2-medicare-levy-surcharge-2026",
    // Whole-of-household cover rule: to be exempt, an appropriate hospital
    // policy must cover the person, their spouse AND all dependants.
    mlsAppropriateCover:
      "https://www.ato.gov.au/individuals-and-families/medicare-and-private-health-insurance/medicare-levy-surcharge/paying-the-medicare-levy-surcharge/appropriate-level-of-private-patient-hospital-cover",
    lhc: "https://www.privatehealth.gov.au/health_insurance/surcharges_incentives/lifetime_health_cover.htm",
  },
  sourcesVerifiedAt: "2026-06-13",
  mls: {
    // Single thresholds 2025-26. Boundaries verified 2026-06-13 against
    // privatehealth.gov.au and Private Healthcare Australia:
    //   Base 0%      ≤ $101,000
    //   Tier 1 1.0%  $101,001–$118,000
    //   Tier 2 1.25% $118,001–$158,000
    //   Tier 3 1.5%  ≥ $158,001
    // CORRECTION (2026-06-13): the Tier 2/3 boundary was previously encoded
    // as $144,001 (matching docs/03-product-spec.md §5.2). That figure is
    // WRONG for FY2025-26 — the authoritative single Tier 3 threshold is
    // $158,001, not $144,001. The spec text (§1.5, §5.2) still carries the
    // stale $144k/$288k family pair and should be corrected by the spec
    // owner; this shipped ruleset now reflects the published ATO/APRA figures.
    single: [
      { from: 0, to: 101_001, rate: 0 },
      { from: 101_001, to: 118_001, rate: 0.01 },
      { from: 118_001, to: 158_001, rate: 0.0125 },
      { from: 158_001, to: null, rate: 0.015 },
    ],
    // Family (couple/family) thresholds 2025-26, before the per-child
    // increment. The increment shifts every boundary up uniformly.
    //   Base 0%      ≤ $202,000
    //   Tier 1 1.0%  $202,001–$236,000
    //   Tier 2 1.25% $236,001–$316,000
    //   Tier 3 1.5%  ≥ $316,001
    // CORRECTION (2026-06-13): the Tier 2/3 boundary was previously $288,001
    // (spec §1.5/§5.2). The authoritative family Tier 3 threshold is
    // $316,001. The ATO "Alice and Adam" worked example pins combined
    // $239,000 in Tier 2, which is only consistent with a Tier 2/3 boundary
    // above $239k (i.e. $316,001), not $288,001.
    family: [
      { from: 0, to: 202_001, rate: 0 },
      { from: 202_001, to: 236_001, rate: 0.01 },
      { from: 236_001, to: 316_001, rate: 0.0125 },
      { from: 316_001, to: null, rate: 0.015 },
    ],
    familyChildIncrement: 1_500,
  },
  lhc: {
    graceAge: 31,
    loadingPerYear: 0.02,
    cap: 0.7,
    dropoffYears: 10,
  },
};
